import { CloseRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  Tooltip,
  Typography
} from "@mui/material";
import moment from "moment";
import FileIcon from "../../../assets/images/file/Paperclip.svg";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import TextEditorQuill from "../../../components/TextEditorQuill";
import { ControlForm, ImageCustom } from "../../../components";
import { DATETIME_FORMAT4, DATE_REQ_FORMAT } from "../../../config/constants";
import { TagInput } from "../../WorkflowManagement/components/TagInput/TagInput";

import InputForm from "../../../components/controls/InputForm";
import { IDocument, IDocumentType } from "../../../models/docs";
import { useEffect, useRef, useState } from "react";
import { useApi } from "../../../hooks/useApi.hook";
import DocsService from "../../../services/api/docs.service";
import {
  documentStatusTitle,
  EDocumentStatus,
  EFileType
} from "../../../models/common/models.enum";
import CheckIcon from "../../../assets/images/common/select_square.svg";
import IconUnCheck from "../../../assets/images/common/check_square_5.svg";
import FileImage from "../../ManageProject/Task/components/CommentTask/components/FileImage";
import UploadFileService from "../../../services/api/uploadFile.service";
import { toggleMessage } from "../../../components/Toast/Toast";
import { IAttachmentFile } from "../../../models/Task";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

type Props = {
  handleClose: Function;
  handleOnSubmit: Function;
  isLoading: boolean;
  editItem?: IDocument;
  documentTypes: IDocumentType[];
  documentTypeId?: string;
};

const CreateDocumentPopup = ({
  handleClose,
  handleOnSubmit,
  isLoading,
  editItem,
  documentTypes,
  documentTypeId
}: Props) => {
  const [files, setFiles] = useState<File[]>([]);
  const [deleteFiles, setDeletefiles] = useState<IAttachmentFile[]>([]);
  const [oldFiles, setOldfiles] = useState<IAttachmentFile[]>(
    editItem?.attachmentFiles ?? []
  );
  const [checkAll, setCheckAll] = useState(editItem?.isViewAll ?? false);
  const inputRef = useRef<any>(null);

  const uploadFile = useApi<IAttachmentFile[]>({});
  const { t, i18n } = useTranslation();

  const [locale, setLocale] = useState<string>();

  useEffect(() => {
    setLocale(i18n.language);
    dayjs.locale(locale);
  }, [i18n.language]);

  const validationSchema = Yup.object().shape({
    title: Yup.string().required(t("Docs.DocumentForm.ErrorTitleRequired")),
    description: Yup.string().required(
      t("Docs.DocumentForm.ErrorContentRequired")
    ),
    dateOfIssue: Yup.string().required(
      t("Docs.DocumentForm.ErrorIssueDateRequired")
    ),
    documentTypeId: Yup.string()
      .notOneOf([""], t("Docs.DocumentForm.ErrorDocumentTypeRequired"))
      .required()
  }) as any;

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors }
  } = useForm<any>({
    defaultValues: editItem ?? {},
    resolver: yupResolver(validationSchema)
  });

  useEffect(() => {
    setValue(
      "reciver",
      editItem?.reciver?.map(reciver => ({
        idEdit: reciver.id,
        id: reciver.userOrDepartmentId,
        userName: reciver.userName,
        fullName: reciver.userName,
        type: reciver.type
      }))
    );

    setValue("documentTypeId", documentTypeId);
  }, [editItem]);

  const onSubmit = (request: IDocument) => {
    request = { ...request, reciver: request.reciver };
    if (checkAll) {
      request = { ...request, reciver: [] };
    }
    if (deleteFiles.length > 0) {
      uploadFile.request(
        UploadFileService.deleteFiles(deleteFiles).then(res => {
          if (res.success) {
            toggleMessage({
              type: "success",
              message: res?.message ?? t("Docs.DocumentForm.DeleteFilesSuccess")
            });
          } else {
            toggleMessage({
              type: "error",
              message: res?.error ?? t("Docs.DocumentForm.DeleteFilesError")
            });
          }
        })
      );
    }
    handleOnSubmit(request);
  };

  return (
    <Box
      sx={{
        backgroundColor: "#f1f5f6",
        padding: "14px",
        position: "absolute",
        top: "50%",
        left: "50%",
        height: "auto",
        transform: "translate(-50%, -50%)",
        width: "80vw",
        borderRadius: "8px"
      }}
    >
      <Box display="flex">
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "8px",
            padding: "16px",
            width: "70%"
          }}
        >
          <Typography variant="h5" marginBottom="14px">
            {editItem
              ? t("Docs.DocumentForm.TitleEdit")
              : t("Docs.DocumentForm.TitleCreate")}
          </Typography>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box display="flex">
              <ControlForm title={t("Docs.DocumentForm.LabelNumberIdentifier")}>
                <InputForm
                  name={"numberIdentifier"}
                  placeholder={t(
                    "Docs.DocumentForm.PlaceholderNumberIdentifier"
                  )}
                  control={control}
                  errors={errors}
                  disabled={isLoading}
                />
              </ControlForm>
              <Box flexGrow={1} marginLeft="14px">
                <ControlForm
                  title={t("Docs.DocumentForm.LabelTitle")}
                  isRequired
                >
                  <InputForm
                    name="title"
                    placeholder={t("Docs.DocumentForm.PlaceholderTitle")}
                    required
                    control={control}
                    errors={errors}
                    disabled={isLoading}
                  />
                </ControlForm>
              </Box>
            </Box>

            <Box display="flex" margin="12px 0">
              <Box flexGrow={1}>
                <ControlForm
                  title={t("Docs.DocumentForm.LabelIssueDate")}
                  isRequired
                >
                  <LocalizationProvider
                    dateAdapter={AdapterMoment}
                    adapterLocale={locale}
                  >
                    <DateTimePicker
                      name="dateOfIssue"
                      ampm={false}
                      format={DATETIME_FORMAT4}
                      disabled={isLoading}
                      defaultValue={
                        editItem ? moment(editItem.dateOfIssue) : undefined
                      }
                      sx={{
                        width: "100%",
                        border: errors.dateOfIssue ? "1px solid red" : "none",
                        borderRadius: "12px"
                      }}
                      onChange={e => {
                        if (e) {
                          setValue("dateOfIssue", e.format(DATE_REQ_FORMAT));
                        }
                      }}
                      dayOfWeekFormatter={day => `${day.format("ddd")}`}
                    />
                  </LocalizationProvider>
                  {errors.dateOfIssue && (
                    <Typography
                      variant="caption"
                      color="error"
                      marginLeft="16px"
                    >
                      {t("Docs.DocumentForm.ErrorIssueDateRequired")}
                    </Typography>
                  )}
                </ControlForm>
              </Box>
              <Box flexGrow={1} marginLeft="12px">
                <ControlForm title={t("Docs.DocumentForm.LabelExpireDate")}>
                  <LocalizationProvider
                    dateAdapter={AdapterMoment}
                    adapterLocale={locale}
                  >
                    <DateTimePicker
                      name="documentExpirationDate"
                      ampm={false}
                      format={DATETIME_FORMAT4}
                      defaultValue={
                        editItem && getValues("documentExpirationDate")
                          ? moment(getValues("documentExpirationDate"))
                          : undefined
                      }
                      disabled={isLoading}
                      sx={{ width: "100%" }}
                      onChange={e => {
                        if (e) {
                          setValue(
                            "documentExpirationDate",
                            e.format(DATE_REQ_FORMAT)
                          );
                        }
                      }}
                      dayOfWeekFormatter={day => `${day.format("ddd")}`}
                    />
                  </LocalizationProvider>
                </ControlForm>
              </Box>
            </Box>
            <ControlForm title={t("Docs.DocumentForm.LabelContent")} isRequired>
              <Controller
                name={"description"}
                render={({ field }) => (
                  <TextEditorQuill
                    placeholder={t("Docs.DocumentForm.PlaceholderContent")}
                    toolbar={true}
                    editorStyle={{
                      border: errors.description
                        ? "1px solid red"
                        : "1px solid #D3DCDF",
                      borderRadius: "8px",
                      width: "100%",
                      height: "40vh"
                    }}
                    onChange={e => field.onChange(e)}
                    value={field.value}
                  />
                )}
                control={control}
              />
              {errors.description && (
                <Typography variant="caption" color="error" marginLeft="16px">
                  {t("Docs.DocumentForm.ErrorContentRequired")}
                </Typography>
              )}
            </ControlForm>
          </form>
        </Box>
        <Box
          width="30%"
          marginLeft="14px"
          display="flex"
          flexDirection="column"
        >
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "16px",
              flex: "0 0 auto"
            }}
          >
            <Box sx={{ opacity: checkAll ? "50%" : "100%" }}>
              <ControlForm
                title={t("Docs.DocumentForm.LabelReceiver")}
                classname="brand-form"
              >
                <TagInput
                  name={"reciver"}
                  control={control}
                  errors={errors}
                  isError={!!errors.reciver}
                  placeholder={t("Docs.DocumentForm.PlaceholderReceiver")}
                  disabled={checkAll}
                />
              </ControlForm>
            </Box>

            <Box display="flex" alignItems="center">
              <Controller
                name={"isViewAll"}
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    checked={field.value}
                    icon={
                      <img src={IconUnCheck} width={"22px"} height={"22px"} />
                    }
                    checkedIcon={
                      <img src={CheckIcon} width={"22px"} height={"22px"} />
                    }
                    onChange={e => {
                      setCheckAll(e.target.checked);
                      field.onChange(e.target.checked);
                    }}
                  />
                )}
              />
              <Typography variant="subtitle2" fontWeight={"400"}>
                {t("Docs.DocumentForm.LabelSendAllCompany")}
              </Typography>
            </Box>
            <Box margin="12px 0">
              <ControlForm
                title={t("Docs.DocumentForm.LabelDocumentType")}
                isRequired
              >
                <Controller
                  name={"documentTypeId"}
                  defaultValue={editItem?.documentTypeId}
                  render={({ field }) => (
                    <Select
                      {...field}
                      disabled={documentTypes.length < 0 || isLoading}
                      fullWidth
                      size="small"
                      placeholder={t("Docs.DocumentForm.PlaceholderSelect")}
                      error={!!errors.documentTypeId}
                      sx={{
                        fontSize: "small",
                        height: "40px"
                      }}
                    >
                      {documentTypes &&
                        documentTypes.map(type => (
                          <MenuItem
                            key={type.id}
                            value={type.id}
                            sx={{ fontSize: "small" }}
                          >
                            {type.name}
                          </MenuItem>
                        ))}
                    </Select>
                  )}
                  control={control}
                />
                {errors.documentTypeId && (
                  <Typography variant="caption" color="error" marginLeft="16px">
                    {t("Docs.DocumentForm.ErrorDocumentTypeRequired")}
                  </Typography>
                )}
              </ControlForm>
            </Box>
            <ControlForm title={t("Docs.DocumentForm.LabelStatus")}>
              <Controller
                name={"status"}
                defaultValue={
                  editItem?.status ?? EDocumentStatus.PendingRelease
                }
                render={({ field }) => (
                  <Select
                    {...field}
                    disabled={isLoading}
                    fullWidth
                    size="small"
                    placeholder={t("Docs.DocumentForm.PlaceholderSelectStatus")}
                    sx={{
                      fontSize: "small",
                      height: "40px"
                    }}
                    error={!!errors.status}
                  >
                    {[
                      EDocumentStatus.PendingRelease,
                      EDocumentStatus.Released
                    ].map(status => (
                      <MenuItem
                        key={status}
                        value={status}
                        sx={{ fontSize: "small" }}
                      >
                        {t(documentStatusTitle(status))}
                      </MenuItem>
                    ))}
                  </Select>
                )}
                control={control}
              />
            </ControlForm>
            <Box height="12px" />
            <ControlForm title={t("Docs.DocumentForm.LabelTags")}>
              <InputForm
                name={"tags"}
                placeholder={t("Docs.DocumentForm.PlaceholderTags")}
                control={control}
                errors={errors}
                disabled={isLoading}
              />
            </ControlForm>
          </Box>
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "16px",
              margin: "14px 0",
              flex: "1 1 auto"
            }}
          >
            <ControlForm
              title={t("Docs.DocumentForm.AttachmentTitle")}
              infor={t("Docs.DocumentForm.AttachmentInfo")}
              classname="brand-form"
            >
              <Box display="flex" flexDirection={"column"}>
                <Grid
                  container
                  sx={{
                    height: "auto",
                    maxHeight: "180px",
                    overflow: "auto"
                  }}
                >
                  {oldFiles.length > 0 &&
                    oldFiles.map((file, i) => (
                      <Grid key={`${i}/${file.fileName}`} item xs={"auto"}>
                        <Box
                          border={"1px solid #DEDEDE"}
                          borderRadius="8px"
                          margin="12px 0"
                          padding="4px 6px"
                          display="flex"
                          marginRight="6px"
                        >
                          <FileImage type={file.extension ?? EFileType.doc} />
                          <Typography variant="caption" margin="0 6px">
                            {file.fileName}
                          </Typography>
                          <Tooltip
                            title={t(
                              "Docs.DocumentForm.AttachmentTooltipDelete"
                            )}
                          >
                            <Button
                              sx={{ color: "#7E969F" }}
                              onClick={() => {
                                setDeletefiles([...deleteFiles, file]);
                                const list = getValues(
                                  "attachmentFiles"
                                ) as IAttachmentFile[];
                                setOldfiles(
                                  oldFiles.filter(o => o.id !== file.id)
                                );
                                setValue(
                                  "attachmentFiles",
                                  list.filter(l => l.id !== file.id)
                                );
                              }}
                            >
                              <CloseRounded sx={{ fontSize: 14 }} />
                            </Button>
                          </Tooltip>
                        </Box>
                      </Grid>
                    ))}

                  {files &&
                    files.map((file, i) => (
                      <Grid key={`${i}/${file.name}`} item xs={"auto"}>
                        <Box
                          border={"1px solid #DEDEDE"}
                          borderRadius="8px"
                          margin="12px 0"
                          padding="4px 6px"
                          display="flex"
                          marginRight="6px"
                        >
                          <FileImage
                            type={
                              `.${file.type.split("/")[1]}` ?? EFileType.doc
                            }
                          />
                          <Typography variant="caption" margin="0 6px">
                            {file.name}
                          </Typography>
                          <Tooltip
                            title={t(
                              "Docs.DocumentForm.AttachmentTooltipDelete"
                            )}
                          >
                            <Button
                              sx={{ color: "#7E969F" }}
                              onClick={() =>
                                setFiles(files.filter(f => f !== file))
                              }
                            >
                              <CloseRounded sx={{ fontSize: 14 }} />
                            </Button>
                          </Tooltip>
                        </Box>
                      </Grid>
                    ))}
                </Grid>

                <Box
                  sx={{
                    border: "1px dashed green",
                    borderRadius: "8px",
                    padding: "10px 0",
                    justifyContent: "center",
                    color: "green",
                    display: "flex",
                    opacity: isLoading ? "40%" : "100%",
                    flex: "0 0 auto"
                  }}
                  onClick={() => (isLoading ? {} : inputRef.current.click())}
                >
                  <ImageCustom src={FileIcon} width="18px" height="18px" />
                  <Typography variant="caption" marginLeft="8px">
                    {t("Docs.DocumentForm.AttachmentSelectButton")}
                  </Typography>
                  <input
                    name="avatar"
                    ref={inputRef}
                    type="file"
                    id="input-file-upload"
                    multiple={true}
                    onChange={e => {
                      if (e.target?.files) {
                        const fileList = files.concat(
                          Array.from(e.target?.files)
                        );
                        setFiles(fileList);
                        setValue("attachmentFiles", [
                          ...(getValues("attachmentFiles") ?? []),
                          ...fileList
                        ]);
                      }
                    }}
                    className="input-file-upload"
                    hidden
                  />
                </Box>
              </Box>
            </ControlForm>
          </Box>
          <Box display="flex" flex={"0 0 auto"}>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                borderRadius: "8px",
                marginRight: "14px",
                color: "#7E969F",
                borderColor: "#7E969F"
              }}
              onClick={() => handleClose()}
              disabled={isLoading}
            >
              {t("Docs.DocumentForm.ButtonCancel")}
            </Button>
            <Button
              variant="contained"
              size="small"
              fullWidth
              sx={{ borderRadius: "8px" }}
              onClick={handleSubmit(onSubmit)}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={20} />
              ) : editItem ? (
                t("Docs.DocumentForm.ButtonUpdate")
              ) : (
                t("Docs.DocumentForm.ButtonCreate")
              )}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CreateDocumentPopup;
